















































import { Vue, Component, Watch } from 'vue-property-decorator';

import flagKeys from '@/constants/flagKeys';

import { Surface, Typography } from '@/components/atoms';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import { BreadcrumbData } from '@/componentsV2/molecules/Breadcrumbs/Breadcrumbs.vue';
import { TDeadLineType } from '@/models/marketingCenter/freeCoins';

import Page from '@/componentsV2/molecules/Page/Page.vue';
import TextField from '@/componentsV2/molecules/Inputs/TextField.vue';
import CampaignSectionSetting from '@/componentsV2/organisms/SellCoins/Campaign/FormSteps/CampaignSectionSetting.vue';
import DateSectionSetting from '@/componentsV2/organisms/SellCoins/Campaign/FormSteps/DateSectionSetting.vue';
import BonusSectionSetting from '@/componentsV2/organisms/SellCoins/Campaign/FormSteps/BonusSectionSetting.vue';
import CampaignSideBar from '@/componentsV2/organisms/SellCoins/Campaign/CampaignSideBar.vue';

import { treatError } from '@/utils';
import DateUtils from '@/utils/DateUtils';
import eventBus from '@/utils/eventBus';
import isMobile from '@/utils/mobile-check';
import { EBusinessRewardsStatus } from '@/models/business';

import SellCoinsService from '@/api/SellCoinsService';
import { EIncentiveType } from '@/models/sell-coins';
import { isRemoteLoading } from '@/utils/remote-loading';

@Component({
  components: {
    Page,
    Surface,
    Typography,
    TextField,
    CampaignSectionSetting,
    BonusSectionSetting,
    DateSectionSetting,
    CampaignSideBar
  }
})
export default class SellingCoinsCampaignCreation extends Vue {
  sellCoinsService = new SellCoinsService();

  loading: boolean = false;
  error: string[] = [];

  campaignName: string = '';
  bonusPercentage: string = '5%';

  expirationType: TDeadLineType = 'no-deadline';
  expiration: string = '';

  sidebarPosition: 'relative' | 'sticky' = 'relative';
  expirationPlaceholderValue = '';

  get isMobileDevice() {
    return isMobile();
  }

  get title() {
    return 'Create new campaign';
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get shouldHideShareLink() {
    return this.flags.includes(flagKeys.hideSellCoinCampaignLinkSharing);
  }

  get isRemoteLoading() {
    return isRemoteLoading();
  }

  clearDeadLineError() {
    if (this.expiration || this.expirationType === 'no-deadline') {
      this.error = this.error.filter(err => err !== 'deadline-date');
    }
  }

  @Watch('expirationType', { deep: true })
  removeDeadlineDateError() {
    this.clearDeadLineError();
  }

  @Watch('expiration', { deep: true })
  removeDeadlineDateErrorByExpiration() {
    this.clearDeadLineError();
  }

  @Watch('campaignName')
  validate() {
    if (this.campaignName.length) {
      this.error = this.error.filter(err => err !== 'campaignName');
    }
  }

  @Watch('bonusPercentage')
  validateBonusPercentage() {
    if (this.bonusPercentage !== 'Other' && this.bonusPercentage !== '%') {
      this.error = this.error.filter(err => err !== 'inputOther');
    }
  }

  handleSaveClick() {
    if (this.campaignCreationValidator()) {
      this.trackingAnalytics();
      this.createNewSellCoinsCampaign();
    }
  }

  campaignCreationValidator() {
    const incentiveValue = parseInt(this.bonusPercentage.replace('%', ''));
    const flags = this.$store.getters['flags/getFlags'] || [];

    const getRewardStatus = this.$store.getters['business/getRewardStatus'];

    const rewardOk =
      !getRewardStatus.rewardStatus ||
      getRewardStatus.rewardStatus !== EBusinessRewardsStatus.INACTIVE;
    const featureFlag = flags.includes(
      flagKeys.dashboardSellCoinsAndLoyaltVerify
    );
    const isFeatureFlagAndLoyaltActive = featureFlag && rewardOk;

    const coinRewardPercentage: string = this.$store.getters[
      'business/getBusinessState'
    ].coinRewardPercentage;

    if (this.campaignName.length == 0) {
      this.error.push('campaignName');
      this.$notification('Campaign name can not be empty.', 'error');
      return;
    }

    if (this.expirationType === 'deadline-date' && !this.expiration) {
      this.error.push('deadline-date');
      this.$notification('End date can not be empty.', 'error');
      return;
    }

    const bonusInvalidValues: string[] = ['Other', '%'];
    const isBonusValueValid = bonusInvalidValues.find(element => {
      return element.toLowerCase() === this.bonusPercentage.toLowerCase();
    });

    if (isBonusValueValid) {
      this.error.push('inputOther');
      this.$notification('A bonus % must be selected.', 'error');
      return;
    }

    if (
      isFeatureFlagAndLoyaltActive &&
      incentiveValue &&
      incentiveValue < parseInt(coinRewardPercentage)
    ) {
      this.$notification('Adjust the bonus rating value.', 'error');
      return;
    }

    return true;
  }

  trackingAnalytics() {
    this.$analytics({
      eventCategory: 'Sell Coins Campaign Creation | Create campaign button',
      eventAction: 'click',
      eventLabel: 'Clicked to save campaign'
    });
  }

  createNewSellCoinsCampaign() {
    this.createSellingCoinsCampaign();
  }

  getNewCampaignData() {
    const newCampaignData: any = {
      name: this.campaignName,
      incentiveValue: parseInt(this.bonusPercentage.replace('%', '')),
      incentiveType: EIncentiveType.BONUS
    };

    if (this.expirationType !== 'no-deadline') {
      newCampaignData.endDate = DateUtils.formattedDate({
        date: this.expiration
      });
    } else {
      newCampaignData.endDate = null;
    }

    return newCampaignData;
  }

  async createSellingCoinsCampaign() {
    this.loading = true;

    try {
      this.sellCoinsService.disableInterception();

      const response = await this.sellCoinsService.createSellingCoinsCampaign(
        this.getNewCampaignData()
      );

      if (response.status === 201) {
        window.parent.postMessage(
          {
            type: 'sell-coins:campaign-created',
            data: response.data
          },
          '*'
        );
        this.$router.push('/sell-coins');

        if (this.shouldHideShareLink) {
          this.$notification('Campaign created successfully!');
          return;
        }
        if (this.isRemoteLoading) {
          return;
        }
        eventBus.$emit(
          'openSubModal',
          new ModalOptionsV2({
            icon: 'none',
            hideCancelButton: true,
            confirmEvent: '',
            componentToRender: 'SellCoinsCampaignCreatedModal'
          }),
          {
            shareLink: response.data.url
          }
        );
      }
    } catch (err) {
      if (err instanceof Error) {
        this.activateNewSellingCoinsCampaign(err);
      }
    }
  }

  activateNewSellingCoinsCampaign(e: any): void {
    const newCampaignData = this.getNewCampaignData();

    if (e.response.status === 409) {
      const activeCampaignData = e.response.data.data;
      activeCampaignData.endDate = DateUtils.formattedDate({
        date: activeCampaignData.endDate
      });

      eventBus.$emit(
        'openSubModal',
        new ModalOptionsV2({
          icon: 'none',
          hideCancelButton: true,
          confirmEvent: '',
          componentToRender: 'SellCoinsCampaignStatusManagementModal'
        }),
        {
          activeCampaignData,
          newCampaignData,
          onConfirm: async () => {
            await this.sellCoinsService.deactivateActiveCampaign();
            this.createNewSellCoinsCampaign();
          }
        }
      );
    } else {
      this.$notification(treatError(e));
    }
  }

  changeExpirationValue(date: string) {
    this.expiration = date;
    this.expirationPlaceholderValue = DateUtils.formattedDate({ date });
  }

  createObserver() {
    const target: HTMLElement = this.$refs.target as HTMLElement;

    if (this.isMobileDevice) return;

    const observer = new IntersectionObserver(async entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.sidebarPosition = 'relative';
        } else {
          this.sidebarPosition = 'sticky';
        }
      });
    });

    if (target) {
      observer.observe(target);
    }
  }

  get businessType() {
    return this.$store.getters['business/getBusinessInfo'].business.type;
  }

  get initialVisibilityType() {
    if (this.businessType === 'physical') return 'local';
    return 'nationwide';
  }

  get isPhysicalOnline() {
    return this.businessType === 'physical_online';
  }

  handleChildModalClose() {
    this.loading = false;
  }

  back() {
    this.$router.push('/sell-coins');
  }

  get breadcrumbs() {
    const breadcrumbList: BreadcrumbData[] = [
      {
        label: 'Overview',
        path: '/sell-coins'
      }
    ];

    if (!this.isMobileDevice) {
      breadcrumbList.push({
        label: 'Create new campaign'
      });
    }

    return breadcrumbList;
  }

  mounted() {
    this.createObserver();
    eventBus.$on('closeModal', this.handleChildModalClose);
  }

  beforeDestroy() {
    eventBus.$off('closeModal', this.handleChildModalClose);
  }
}
